import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";
import { PREDEFINED_METRIC_OPTIONS } from "src/pages/analytics/constants";
import { useChartDependencies } from "src/pages/analytics/state/use-chart-dependencies";
import type { MetricSelectorOptions, Option, OptionGroup } from "./types";
import {
  buildEventModelAndColumnOptions,
  buildMetricOptions,
  buildParentModelColumnOptions,
  buildParentModelOptions,
  buildRelatedModelAndColumnOptions,
  buildTraitOptions,
} from "./utils";

type UseMetricSelectorOptionsParams = {
  parentModelId: string | null;
  includePredefinedMetrics?: boolean;
};

export const useMetricSelectorOptions = ({
  parentModelId,
  includePredefinedMetrics = false,
}: UseMetricSelectorOptionsParams): MetricSelectorOptions => {
  const { restrictMergedColumns } = useFlags();
  const {
    parentModel,
    events: eventRelationships,
    metricOptions: metricDefinitions,
    traits,
    relatedModels: relatedModelRelationships,
  } = useChartDependencies({
    parentModelId: Number(parentModelId),
  });

  // NOTE: We always call the parent model "Member" in the selector
  // to keep the UI clean. Some people have incredibly long parent model names
  const parentModelName = "Member";
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const parentModelOptions = useMemo<Option[]>(
    () =>
      buildParentModelOptions({
        parentModelName,
        parentModelId,
      }),
    [parentModelId, parentModelName],
  );

  const parentModelColOptions = useMemo<Option[]>(
    () =>
      buildParentModelColumnOptions({
        parentModel,
        restrictMergedColumns,
      }),
    [parentModel, restrictMergedColumns],
  );

  const {
    eventModelOptions,
    eventModelColOptions,
    messageEventModelOptions,
    messageEventModelColOptions,
  } = useMemo<{
    eventModelOptions: Option[];
    eventModelColOptions: OptionGroup[];
    messageEventModelOptions: OptionGroup[];
    messageEventModelColOptions: OptionGroup[];
  }>(
    () => buildEventModelAndColumnOptions(eventRelationships),
    [eventRelationships],
  );

  const metricOptions = useMemo<Option[]>(
    () =>
      buildMetricOptions(
        includePredefinedMetrics
          ? [...PREDEFINED_METRIC_OPTIONS, ...metricDefinitions]
          : metricDefinitions,
      ),
    [metricDefinitions, includePredefinedMetrics],
  );

  const { relatedModelOptions, relatedModelColOptions } = useMemo<{
    relatedModelOptions: Option[];
    relatedModelColOptions: OptionGroup[];
  }>(
    () => buildRelatedModelAndColumnOptions(relatedModelRelationships),
    [relatedModelRelationships],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const traitOptions = useMemo<Option[]>(
    () =>
      buildTraitOptions({
        traits,
        parentModelId,
        parentModelName,
      }),
    [traits, parentModelId, parentModelName],
  );

  return {
    parentModelId: parentModelId ?? "",
    parentModelName: parentModelName,
    parentModelOptions,
    parentModelColOptions,
    eventModelOptions,
    eventModelColOptions,
    messageEventModelOptions,
    messageEventModelColOptions,
    metricOptions,
    traitOptions,
    relatedModelOptions,
    relatedModelColOptions,
  };
};
